import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { formatDate, parseDateKey } from '../utils/dateFormat';
import { handleHybridPdfDownload } from '../utils/downloadUtils';
import { generateHistoryReportPdf } from '../utils/reportGenerator';

interface Log {
    id: number;
    action: string;
    description?: string; // API returns 'description', not 'details'
    details?: string; // Keeping for backward compatibility if needed
    created_at: string;
    user?: {
        name: string;
        avatar_url?: string;
        email?: string;
    };
    meta_data?: any; // API returns 'meta_data'
    metadata?: any;
}

interface Assignment {
    id: number;
    user_id: number;
    assigned_by: number;
    assigned_at: string;
    comment: string;
    completed_at?: string;
    user: {
        name: string;
        avatar_url?: string;
    };
    assigner: {
        name: string;
    };
}

interface Version {
    id: number;
    version_number: number;
    file_path: string;
    file_name: string;
    created_at: string;
    note?: string; // Added note support
    is_current: boolean;
    created_by?: any; // Backend returns object {id, name, email}
    creator?: {
        name: string;
        avatar_url?: string;
    };
}

interface ReviewComment {
    id: number;
    content: string;
    created_at: string;
    user?: {
        name: string;
        avatar_url?: string;
        email?: string;
    };
    created_by?: {
        name: string;
        avatar_url?: string;
        email?: string;
    };
    is_resolved?: boolean;
}

interface HistorySidebarProps {
    taskId: number;
    task?: {
        title: string;
        description?: string;
        status?: { name: string };
        created_at?: string;
        creator?: { name: string; email?: string };
        template?: { name?: string; title?: string };
        agreement_no?: string;
        category?: { name: string };
        agreement_type?: { name: string };
        company?: { name: string };
        template_type?: string;
        updated_at?: string;
        agreement_value?: string;
        period?: string;
        payment_method?: string;
        guarantee?: string;
        penalty_compensation?: string;
    };
    logs: Log[];
    assignments?: Assignment[];
    versions?: Version[];
    reviewComments?: ReviewComment[];
    isOpen: boolean;
    onToggle: () => void;
    loading?: boolean;
    statuses?: any[];
}

type HistoryItem =
    | { type: 'log', data: Log }
    | { type: 'assignment', data: Assignment }
    | { type: 'version', data: Version }
    | { type: 'review_comment', data: ReviewComment };

const HistorySidebar: React.FC<HistorySidebarProps> = ({ taskId, task, logs, assignments = [], versions = [], reviewComments = [], isOpen, onToggle, loading = false, statuses = [] }) => {
    const { darkMode } = useTheme();

    // Combine and sort logs, assignments, versions, and review comments
    const historyItems: HistoryItem[] = [
        // create_task_assignment & create_review_comment are already rendered from their own tables
        ...logs
            .filter(log => !['CREATE_TASK_ASSIGNMENT', 'CREATE_REVIEW_COMMENT'].includes((log.action || '').toUpperCase()))
            .map(log => ({ type: 'log' as const, data: log })),
        ...assignments.map(assignment => ({ type: 'assignment' as const, data: assignment })),
        ...versions.map(version => ({ type: 'version' as const, data: version })),
        ...reviewComments.map(comment => ({ type: 'review_comment' as const, data: comment }))
    ].sort((a, b) => {
        const getTime = (item: HistoryItem) => {
            let dateStr = '';
            if (item.type === 'log') dateStr = item.data.created_at;
            else if (item.type === 'version') dateStr = item.data.created_at;
            else if (item.type === 'assignment') dateStr = item.data.assigned_at;
            else if (item.type === 'review_comment') dateStr = item.data.created_at;

            const date = parseDateKey(dateStr);
            return date ? date.getTime() : 0;
        };

        // Primary sort: "CREATE_TASK" should always be the oldest (last)
        const isCreateA = a.type === 'log' && (a.data as Log).action?.toUpperCase() === 'CREATE_TASK';
        const isCreateB = b.type === 'log' && (b.data as Log).action?.toUpperCase() === 'CREATE_TASK';

        if (isCreateA && !isCreateB) return 1; // A is create -> A goes after B
        if (!isCreateA && isCreateB) return -1; // B is create -> B goes after A

        const timeA = getTime(a);
        const timeB = getTime(b);

        if (timeA !== timeB) {
            return timeB - timeA;
        }

        // Tertiary sort: ID descending (newer ID first)
        return (b.data.id || 0) - (a.data.id || 0);
    });

    // Helper to get status color
    const getStatusColor = (statusName: string, statusId?: number) => {
        if (!statusName) return 'bg-gray-100 text-gray-600';

        // Try to find the status in the provided statuses array
        const status = statuses.find(s =>
            (statusId && s.id === statusId) ||
            (s.name.toUpperCase() === statusName.toUpperCase())
        );

        if (status?.color_class) {
            return status.color_class;
        }

        const name = statusName.toUpperCase();
        if (name === 'NEW' || name === 'DRAFTING') return 'bg-gray-100 text-gray-800';
        if (name === 'REVIEW') return 'bg-orange-100 text-orange-800';
        if (name === 'APPROVED') return 'bg-green-100 text-green-800';
        if (name === 'RELEASED') return 'bg-blue-100 text-blue-800';
        if (name === 'REJECTED') return 'bg-red-100 text-red-800';

        return 'bg-gray-100 text-gray-600';
    };

    // Helper to parse log details
    const renderLogDetails = (log: Log) => {
        const action = log.action.toUpperCase();
        const description = log.description || log.details || '';
        const meta = log.meta_data || log.metadata || {};

        // Status Change
        if ((action === 'UPDATE_STATUS' || action === 'MOVE_TASK_STATUS') && meta.old_status_name && meta.new_status_name) {
            return (
                <div className="flex items-center gap-2 mt-1 text-xs">
                    <span className={`px-2 py-0.5 rounded-full font-medium ${getStatusColor(meta.old_status_name)}`}>
                        {meta.old_status_name}
                    </span>
                    <i className={`fa-solid fa-arrow-right text-gray-400`} />
                    <span className={`px-2 py-0.5 rounded-full font-medium ${getStatusColor(meta.new_status_name, meta.new_status_id)}`}>
                        {meta.new_status_name}
                    </span>
                </div>
            );
        }

        // Try regex fallback for status change if metadata is missing/incomplete
        if (action === 'UPDATE_STATUS' || action === 'MOVE_TASK_STATUS' || description.includes('Status changed')) {
            const match = description.match(/from '(.+?)' to '(.+?)'/i);
            if (match) {
                // ... (existing regex logic)
                return (
                    <div className="flex items-center gap-2 mt-1 text-xs">
                        <span className={`px-2 py-0.5 rounded-full font-medium ${getStatusColor(match[1])}`}>
                            {match[1]}
                        </span>
                        <i className={`fa-solid fa-arrow-right text-gray-400`} />
                        <span className={`px-2 py-0.5 rounded-full font-medium ${getStatusColor(match[2])}`}>
                            {match[2]}
                        </span>
                    </div>
                );
            }
        }

        // Comments / mentions / assignments: show content and mentioned users
        const mentions: { type: string; name: string }[] = Array.isArray(meta.mentions) ? meta.mentions : [];
        const extraText: string | undefined = meta.content || meta.comment;
        if (mentions.length > 0 || extraText) {
            return (
                <div className="mt-0.5">
                    {description && (
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{description}</p>
                    )}
                    {extraText && (
                        <p className={`text-xs mt-2 italic ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>"{extraText}"</p>
                    )}
                    {mentions.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {mentions.map((m, i) => (
                                <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-100 text-indigo-700">
                                    @{m.name}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        // Default: just show description
        if (description) {
            return (
                <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {description}
                </p>
            );
        }
        return null;
    };


    return (
        <>
            {/* Sidebar Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
                    onClick={onToggle}
                />
            )}

            {/* Sidebar Panel */}
            <div className={`fixed inset-y-0 right-0 w-80 bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'
                } ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="h-full flex flex-col">
                    {/* Header */}
                    <div className={`px-4 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div className="flex justify-between items-center mb-3">
                            <h2 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                Log Aktivitas
                            </h2>
                            <button
                                onClick={onToggle}
                                className={`p-2 rounded-md hover:bg-gray-100 ${darkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500'
                                    }`}
                            >
                                <span className="sr-only">Close</span>
                                <i className="fa-solid fa-xmark text-lg"></i>
                            </button>
                        </div>

                        {/* Download Report Button */}
                        <button
                            onClick={() => generateHistoryReportPdf(historyItems, {
                                title: task?.title || `Task_${taskId}`,
                                description: task?.description,
                                status: task?.status?.name,
                                created_at: task?.created_at,
                                creator: task?.creator,
                                template: task?.template ? { name: task.template.title || task.template.name || '' } : undefined,
                                agreement_no: task?.agreement_no,
                                category: task?.category?.name,
                                agreement_type: task?.agreement_type?.name,
                                company: task?.company?.name,
                                template_type: task?.template_type,
                                updated_at: task?.updated_at,
                                agreement_value: task?.agreement_value,
                                period: task?.period,
                                payment_method: task?.payment_method,
                                guarantee: task?.guarantee,
                                penalty_compensation: task?.penalty_compensation
                            })}
                            className={`w-full py-2 px-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${darkMode
                                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                }`}
                            disabled={loading || historyItems.length === 0}
                        >
                            <i className="fa-solid fa-file-pdf"></i>
                            Download Report
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {loading ? (
                            <div className="flex justify-center py-8">
                                <i className={`fa-solid fa-circle-notch fa-spin text-2xl ${darkMode ? 'text-gray-400' : 'text-indigo-600'
                                    }`}></i>
                            </div>
                        ) : historyItems.length === 0 ? (
                            <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                <i className="fa-regular fa-clock text-4xl mb-2 opacity-50"></i>
                                <p>Belum ada aktivitas</p>
                            </div>
                        ) : (
                            <div className="space-y-6 relative">
                                {/* Timeline line */}
                                <div className={`absolute top-0 bottom-0 left-3.5 w-0.5 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'
                                    }`}></div>

                                {historyItems.map((item, index) => {
                                    if (item.type === 'log') {
                                        const log = item.data;
                                        const user = log.user || {
                                            name: log.meta_data?.user_name || 'System',
                                            email: log.meta_data?.user_email
                                        };

                                        return (
                                            <div key={`log-${log.id}`} className="relative pl-8 pb-4">
                                                {/* Timeline dot */}
                                                <div className={`absolute left-0 top-1.5 w-3 h-3 rounded-full border-2 ${darkMode ? 'border-gray-800 bg-purple-500' : 'border-white bg-purple-600'
                                                    } z-10 box-content`}>
                                                </div>

                                                <div className="flex flex-col gap-1">
                                                    {/* Action Title */}
                                                    <h3 className={`text-xs font-bold uppercase tracking-wide ${darkMode ? 'text-purple-400' : 'text-purple-600'
                                                        }`}>
                                                        {log.action.replace(/_/g, ' ')}
                                                    </h3>

                                                    {/* User Info & Date */}
                                                    <div className="flex flex-col mb-1">
                                                        <span className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                                            {user.name}
                                                        </span>
                                                        {user.email && (
                                                            <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                                                {user.email}
                                                            </span>
                                                        )}
                                                        <span className={`text-[10px] mt-0.5 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                                                            {formatDate(log.created_at)}
                                                        </span>
                                                    </div>

                                                    {/* Details Card */}
                                                    <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100 shadow-sm'
                                                        }`}>
                                                        {renderLogDetails(log)}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    } else if (item.type === 'assignment') {
                                        const assignment = item.data;
                                        return (
                                            <div key={`assign-${assignment.id}`} className="relative pl-8 pb-4">
                                                <div className={`absolute left-0 top-1.5 w-3 h-3 rounded-full border-2 ${darkMode ? 'border-gray-800 bg-indigo-500' : 'border-white bg-indigo-600'
                                                    } z-10 box-content`}>
                                                </div>

                                                <div className="flex flex-col gap-1">
                                                    <h3 className={`text-xs font-bold uppercase tracking-wide ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                                                        TASK ASSIGNMENT
                                                    </h3>

                                                    <div className="flex flex-col mb-1">
                                                        <span className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                                            {assignment.assigner?.name || 'Unknown'}
                                                        </span>
                                                        <span className={`text-[10px] ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                                                            {formatDate(assignment.assigned_at)}
                                                        </span>
                                                    </div>

                                                    <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`}>
                                                        <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                                            Assigned to <span className="font-semibold text-indigo-500">{assignment.user?.name || 'Unknown'}</span>
                                                        </p>
                                                        {assignment.comment && (
                                                            <p className={`text-xs mt-2 italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                "{assignment.comment}"
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    } else if (item.type === 'version') {
                                        const version = item.data;
                                        return (
                                            <div key={`ver-${version.id}`} className="relative pl-8 pb-4">
                                                <div className={`absolute left-0 top-1.5 w-3 h-3 rounded-full border-2 ${darkMode ? 'border-gray-800 bg-purple-500' : 'border-white bg-purple-600'
                                                    } z-10 box-content`}>
                                                </div>

                                                <div className="flex flex-col gap-1">
                                                    <h3 className={`text-xs font-bold uppercase tracking-wide ${darkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                                        DOCUMENT VERSION
                                                    </h3>

                                                    <div className="flex flex-col mb-1">
                                                        <span className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                                            {version.created_by?.name || version.creator?.name || 'Unknown'}
                                                        </span>
                                                        <span className={`text-[10px] ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                                                            {formatDate(version.created_at)}
                                                        </span>
                                                    </div>

                                                    <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`}>
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-600'}`}>
                                                                <span className="text-xs font-bold">v{version.version_number}</span>
                                                            </div>
                                                            <div className="flex flex-col flex-1 min-w-0">
                                                                <span className={`text-sm font-medium truncate ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                                                    {version.file_name}
                                                                </span>
                                                                {version.is_current && (
                                                                    <span className="text-[10px] font-medium text-green-500">
                                                                        Current Version
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {/* PDF Download Button */}
                                                            {version.file_name.toLowerCase().endsWith('.docx') && (
                                                                <button
                                                                    onClick={() => handleHybridPdfDownload(taskId, version.id, version.file_name)}
                                                                    className={`p-1.5 rounded-md transition-colors ${darkMode
                                                                        ? 'hover:bg-gray-700 text-gray-400 hover:text-red-400'
                                                                        : 'hover:bg-indigo-100 text-gray-400 hover:text-red-500'
                                                                        }`}
                                                                    title="Download as PDF"
                                                                >
                                                                    <i className="fa-solid fa-file-pdf"></i>
                                                                </button>
                                                            )}
                                                        </div>
                                                        {version.note && (
                                                            <div className={`mt-2 text-xs p-2 rounded ${darkMode ? 'bg-gray-700/50 text-gray-300' : 'bg-gray-50 text-gray-600'}`}>
                                                                <span className="font-medium block mb-0.5">Note:</span>
                                                                {version.note}
                                                            </div>
                                                        )}

                                                        <button
                                                            onClick={() => handleHybridPdfDownload(taskId, version.id, version.file_name)}
                                                            className="mt-3 text-xs flex items-center gap-1.5 text-indigo-500 hover:text-indigo-600 font-medium"
                                                        >
                                                            <i className="fa-solid fa-download"></i>
                                                            Download
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    } else {
                                        // Review Comment
                                        const comment = item.data;
                                        const user = comment.user || comment.created_by || { name: 'Unknown' };

                                        return (
                                            <div key={`review-comment-${comment.id}`} className="relative pl-8 pb-4">
                                                <div className={`absolute left-0 top-1.5 w-3 h-3 rounded-full border-2 ${darkMode ? 'border-gray-800 bg-orange-500' : 'border-white bg-orange-600'
                                                    } z-10 box-content`}>
                                                </div>

                                                <div className="flex flex-col gap-1">
                                                    <h3 className={`text-xs font-bold uppercase tracking-wide ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                                                        REVIEW COMMENT
                                                    </h3>

                                                    <div className="flex flex-col mb-1">
                                                        <span className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                                            {user.name}
                                                        </span>
                                                        <span className={`text-[10px] ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                                                            {formatDate(comment.created_at)}
                                                        </span>
                                                    </div>

                                                    <div className={`p-3 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100 shadow-sm'}`}>
                                                        <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                                            {comment.content}
                                                        </p>
                                                        {comment.is_resolved && (
                                                            <div className="mt-2 flex items-center">
                                                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] rounded-full font-medium">
                                                                    <i className="fa-solid fa-check mr-1"></i> Resolved
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default HistorySidebar;
